import { t } from "../../i18n";
import type { OrganizationCapabilities } from "@lumen/shared";

export const showLegacyOrganizationSections = false;
export const defaultOrganizationCapabilities: OrganizationCapabilities = {
  personalProviders: true,
  organizationProviders: false,
  personalWorkspaces: true,
  sshWorkspaces: false,
  unrestrictedSshHosts: false,
  privateNetwork: false,
  customProviderEndpoints: false,
  unrestrictedProviderHosts: false,
  unrestrictedOutboundHosts: false,
  customWebhooks: false,
  customerManagedConnectors: false,
  allowInternetEgress: false,
};
export const defaultOrganizationEntitlements: OrganizationEntitlements = {
  executionEnabled: true,
  allToolsEnabled: false,
  features: {},
  maxSeats: null,
  monthlyPromptLimit: null,
  monthlySpendMicros: null,
  maxConcurrentGenerations: 10,
  maxConcurrentProviderDiscoveries: 2,
  maxToolCallsPerGeneration: 100,
  maxSubagentFanout: 10,
  maxWorkspaceRuntimeSeconds: 3600,
  maxConcurrentSshConnections: 4,
  maxSshConnectionAttemptsPerMinute: 20,
  storageLimitBytes: null,
  trialEndsAt: null,
  graceEndsAt: null,
};

export type Row = {
  organization: {
    id: string;
    name: string;
    slug: string;
    state: string;
    locale: string;
    currency: string;
    timezone: string;
    retentionDays: number;
  };
  memberCount: number;
  promptCount: number;
  billingStatus: string | null;
  subscriptionState: string | null;
  securityStatus: "configured" | "attention";
};
export type Forge = {
  id: string;
  name: string;
  host: string;
  kind: string;
  enabled: boolean;
  credentialConfigured: boolean;
  oauthCallbackUrl?: string;
};
export type AccessRow = {
  providerId: string;
  providerName: string;
  providerEnabled: boolean;
  modelId: string;
  modelName: string;
  modelEnabled: boolean;
  entitlementEnabled: boolean | null;
  defaultEffect: "allow" | "deny" | null;
};
export type BillingInformation = {
  legalName: string | null;
  billingEmail: string | null;
  taxId: string | null;
  status: string;
};
export type Member = {
  user: { id: string; name: string; email: string };
  membership: { role: string; state: string };
  permissions: string[] | null;
};
export type Audit = {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  createdAt: string;
};
export type OrganizationEntitlements = {
  executionEnabled: boolean;
  allToolsEnabled: boolean;
  features: Record<string, boolean>;
  maxSeats: number | null;
  monthlyPromptLimit: number | null;
  monthlySpendMicros: number | null;
  maxConcurrentGenerations: number | null;
  maxConcurrentProviderDiscoveries: number | null;
  maxToolCallsPerGeneration: number | null;
  maxSubagentFanout: number | null;
  maxWorkspaceRuntimeSeconds: number | null;
  maxConcurrentSshConnections: number | null;
  maxSshConnectionAttemptsPerMinute: number | null;
  storageLimitBytes: number | null;
  trialEndsAt: string | null;
  graceEndsAt: string | null;
};
export type OrganizationPricing = {
  configured: boolean;
  currency: string;
  version: number;
  effectiveFrom: string | null;
  prices: {
    baseFeeMicros: number;
    seatFeeMicros: number;
    includedSeats: number;
    promptFeeMicros: number;
    includedPrompts: number;
    modelCostMarkupBps: number;
    workspaceCpuSecondMicros: number;
    storageGbDayMicros: number;
    automationRunMicros: number;
    toolExecutionMicros: number;
    imageGenerationMicros: number;
    workspaceMemoryMbSecondMicros: number;
    minimumFeeMicros: number;
    discountBps: number;
    taxBps: number;
  };
};
export type OrganizationResource = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
};
export type OrganizationIntegration = {
  id: string;
  name: string;
  kind: string;
  scope: string;
  capabilities: string[];
  enabled: boolean;
  lastPolledAt: string | null;
  lastError: string | null;
};
export type OrganizationResources = Record<
  "skills" | "agents" | "tools" | "workspaces" | "repositories" | "automations",
  OrganizationResource[]
>;
export type CatalogResource = {
  resourceType: "tool" | "agent" | "skill" | "workspace" | "mcp_server";
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  entitlementEnabled: boolean | null;
};
export type OrganizationToolSetting = {
  id: string;
  name: string;
  displayName: string;
  adminSchema: Record<string, unknown>;
  enabled: boolean;
  config: Record<string, unknown>;
  configured: boolean;
};
export const capabilityDefinitions: Array<{
  key: keyof OrganizationCapabilities;
  title: string;
  description: string;
  group: "Standard" | "Enterprise approval" | "Connector required";
  risk: "standard" | "high";
  requires?: keyof OrganizationCapabilities;
}> = [
  {
    key: "personalProviders",
    title: "Personal providers",
    description:
      "Allow members to connect private providers from the approved catalog.",
    group: "Standard",
    risk: "standard",
  },
  {
    key: "personalWorkspaces",
    title: "Personal workspaces",
    description:
      "Allow personal workspace usage under the standard runtime controls.",
    group: "Standard",
    risk: "standard",
  },
  {
    key: "organizationProviders",
    title: "Organization providers",
    description:
      "Allow organization-owned providers from the approved provider catalog.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "customProviderEndpoints",
    title: "Custom provider endpoints",
    description:
      "Allow reviewed custom endpoints; HTTPS, allowlists, SSRF checks, and quotas still apply.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "unrestrictedProviderHosts",
    title: "Unrestricted public provider hosts",
    description:
      "Allow provider endpoints on any public HTTPS hostname. Private, metadata, reserved, unsafe-port, DNS-rebinding, quota, and SSRF protections still apply.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "unrestrictedOutboundHosts",
    title: "Unrestricted public outbound hosts",
    description:
      "Allow outbound integrations and HTTP requests to any public HTTPS hostname. Private, metadata, reserved, unsafe-port, DNS-rebinding, quota, and SSRF protections still apply.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "unrestrictedSshHosts",
    title: "Unrestricted public SSH hosts",
    description:
      "Allow SSH and Proxmox connections to any public hostname. Host-key pinning, public-address validation, port controls, quotas, rate limits, command policy, and session kill switches still apply.",
    group: "Enterprise approval",
    risk: "high",
    requires: "sshWorkspaces",
  },
  {
    key: "customWebhooks",
    title: "Custom webhooks",
    description:
      "Allow reviewed webhook destinations with signing, replay protection, and quotas.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "sshWorkspaces",
    title: "Managed SSH workspaces",
    description:
      "Only for approved enterprise connections with pinned hosts, command policy, and kill switches.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "privateNetwork",
    title: "Private network access",
    description:
      "Allow providers, tools, integrations, browsers, mail, Git, SSH, and Proxmox to reach approved RFC1918 and private IPv6 destinations. Host approvals and workspace network settings still apply.",
    group: "Enterprise approval",
    risk: "high",
  },
  {
    key: "customerManagedConnectors",
    title: "Customer-managed connectors",
    description:
      "Allow an authenticated outbound tunnel established from the customer network.",
    group: "Connector required",
    risk: "high",
  },
  {
    key: "allowInternetEgress",
    title: "Workspace internet egress",
    description:
      "High risk and unavailable to ordinary organization administrators.",
    group: "Enterprise approval",
    risk: "high",
  },
];

export function organizationResourceLabel(type: string) {
  if (type === "tools" || type === "tool") return t("Tools");
  if (type === "agents" || type === "agent") return t("Agents");
  if (type === "skills" || type === "skill") return t("Skills");
  if (type === "workspaces" || type === "workspace") return t("Workspaces");
  if (type === "repositories") return t("Repositories");
  if (type === "automations") return t("Automations");
  return t("MCP servers");
}
